/** biome-ignore-all lint/performance/noNamespaceImport: ok */
"use client";
import { Form } from "@base-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Info } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import image from "#/assets/images/calc-bmi.png";
import { BmiAction } from "@/app/(main)/calculations/_components/action";
import type { BodyMassIndexResponse } from "@/app/(main)/calculations/_components/types";
import PageHeader from "@/components/layout/page-header";
import { Button } from "@/components/ui/button";
import ExtendedImage from "@/components/ui/extended-image";
import * as Field from "@/components/ui/form/field";
import {
  type BodyMassIndexModel,
  BodyMassIndexSchema,
} from "../_components/types";
import { ResultDialog } from "./_components/result-dialog";

const PercentileCalculation = () => {
  const { control, handleSubmit } = useForm<BodyMassIndexModel>({
    resolver: zodResolver(BodyMassIndexSchema),
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [result, setResult] = useState<BodyMassIndexResponse | null>(null);

  const onSubmit = async (data: BodyMassIndexModel) => {
    const response = await BmiAction(data);
    setResult(response.result);
    setDialogOpen(true);
  };

  return (
    <div className="flex flex-col gap-4 justify-center mb-20">
      <PageHeader title="Beden Kitle İndeksi Hesaplama" />
      <div className="rounded-full border border-gray-300 size-56 mx-auto mb-10">
        <ExtendedImage
          alt="ovum"
          className="rounded-full size-56 "
          height={224}
          src={image}
          width={224}
        />
      </div>
      <Form
        className="w-96 mx-auto flex flex-col gap-4"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Controller
          control={control}
          name="heightCm"
          render={({ field, fieldState }) => {
            return (
              <Field.Root
                dirty={fieldState.isDirty}
                invalid={fieldState.invalid}
                name={field.name}
                touched={fieldState.isTouched}
              >
                <Field.Label>Boyunuz</Field.Label>
                <Field.Control
                  onBlur={field.onBlur}
                  onValueChange={field.onChange}
                  placeholder="165 cm"
                  ref={field.ref}
                  type="number"
                  value={field.value}
                />
                <Field.Description className="flex flex-row justify-center items-center gap-2">
                  <Info className="size-4 shrink-0 text-gray-500" />
                  cm cinsinden giriniz
                </Field.Description>
                <Field.Error match={!!fieldState.error}>
                  {fieldState.error?.message}
                </Field.Error>
              </Field.Root>
            );
          }}
        />
        <Controller
          control={control}
          name="weightKg"
          render={({ field, fieldState }) => {
            return (
              <Field.Root
                dirty={fieldState.isDirty}
                invalid={fieldState.invalid}
                name={field.name}
                touched={fieldState.isTouched}
              >
                <Field.Label>Kilonuz</Field.Label>
                <Field.Control
                  onBlur={field.onBlur}
                  onValueChange={field.onChange}
                  placeholder="55 Kg"
                  ref={field.ref}
                  type="number"
                  value={field.value}
                />
                <Field.Description className="flex flex-row justify-center items-center gap-2">
                  <Info className="size-4 shrink-0 text-gray-500" />
                  Kilogram cinsinden giriniz
                </Field.Description>
                <Field.Error match={!!fieldState.error}>
                  {fieldState.error?.message}
                </Field.Error>
              </Field.Root>
            );
          }}
        />

        <Button type="submit">Hesapla</Button>
      </Form>

      <ResultDialog
        onOpenChange={setDialogOpen}
        open={dialogOpen}
        result={result}
      />
    </div>
  );
};

export default PercentileCalculation;
